import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, Loader2, Lock } from 'lucide-react';
import { sectionMeta, subjectMeta } from '../lib/section-meta';
import { accent, springs } from '../lib/utils-app';
import api from '../lib/api';

export default function SubjectLibrary() {
  const { subjectId } = useParams();
  const subMeta = subjectMeta(subjectId);
  const SubIcon = subMeta.icon;
  const subA = accent(subMeta.accent);

  const [sections, setSections] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.listSections(subjectId), api.listSubjects()])
      .then(([secs, subs]) => {
        if (!alive) return;
        setSections(secs);
        setSubjects(subs);
      })
      .catch((err) => alive && setError(err.message || 'Failed to load'));
    return () => { alive = false; };
  }, [subjectId]);

  const subject = subjects.find((s) => s.id === subjectId);
  const isPreview = subject?.status === 'preview';
  const subjectName = subject?.name || subjectId.replace(/\b\w/g, (c) => c.toUpperCase());
  const totalQs = (sections || []).reduce((s, x) => s + x.count, 0);

  return (
    <div>
      <Link to="/" className="inline-flex items-center text-[12.5px] text-slate-500 hover:text-slate-200 transition mb-6">
        <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Subjects
      </Link>

      <motion.header
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={springs.slow}
        className="relative overflow-hidden rounded-2xl glass hairline px-7 py-7 mb-8"
      >
        <div className={`absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gradient-to-br ${subA.grad} blur-2xl opacity-90`} />
        <div className="relative flex items-center gap-5">
          <div className={`w-14 h-14 rounded-2xl ${subA.soft} ring-1 ${subA.ring} flex items-center justify-center ${subA.text}`}>
            <SubIcon className="w-7 h-7" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">JEE Advanced · Subject</div>
            <h1 className="font-serif-q text-[30px] tracking-tight text-slate-50 leading-tight">{subjectName}</h1>
            {isPreview && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded-md bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20">
                <Lock className="w-3 h-3" /> Coming soon
              </div>
            )}
          </div>
          <div className="ml-auto text-right shrink-0">
            <div className="font-serif-q text-3xl text-slate-100">{sections ? totalQs : '—'}</div>
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500">questions</div>
          </div>
        </div>
      </motion.header>

      {error && <div className="rounded-xl glass hairline p-6 text-rose-300">Failed to load: {error}</div>}
      {!sections && !error && (
        <div className="rounded-2xl glass hairline p-12 flex items-center justify-center text-slate-400 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading chapters…
        </div>
      )}
      {sections && sections.length === 0 && (
        <div className="text-center py-16 text-slate-500 rounded-2xl glass hairline">No chapters yet.</div>
      )}
      {sections && sections.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((s, i) => <SectionCard key={s.id} subjectId={subjectId} section={s} delay={i * 0.04} />)}
        </div>
      )}
    </div>
  );
}

function SectionCard({ subjectId, section, delay }) {
  const meta = sectionMeta(section.id, subjectId);
  const a = accent(meta.accent);
  const Icon = meta.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.soft, delay }} whileHover={{ y: -3 }}
    >
      <Link to={`/subject/${subjectId}/section/${section.id}`}
        className="group relative block rounded-2xl glass hairline p-6 overflow-hidden h-full transition-colors hover:bg-white/[0.04]">
        <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br ${a.grad} blur-2xl opacity-80 group-hover:opacity-100 transition-opacity`} />
        <div className="relative">
          <div className={`w-11 h-11 rounded-xl ${a.soft} ring-1 ${a.ring} flex items-center justify-center ${a.text}`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="mt-5 flex items-center justify-between">
            <div className="font-serif-q text-[20px] text-slate-100 tracking-tight">{section.name}</div>
            <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-slate-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
          </div>
          <p className="text-[13px] text-slate-400 mt-1.5 leading-relaxed">{meta.blurb}</p>
          <div className="mt-5 flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-slate-500">
            <span>{section.count} questions</span>
            <span className={`flex items-center gap-1 ${a.text}`}><span className={`w-1 h-1 rounded-full ${a.dot}`} />Open</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
